<template>
  <Component :is="type" :class="className" role="option" v-bind="rootProps" v-on="$listeners">
    <slot />
  </Component>
</template>

<script lang="ts" setup>
import { computed } from '@nuxtjs/composition-api';

type DropdownItemProps = {
  custom?: boolean;
  separated?: boolean;
  muted?: boolean;
  selected?: boolean;
  external?: boolean;
  link?: string;
};

const props = withDefaults(defineProps<DropdownItemProps>(), {
  custom: false,
  separated: false,
  muted: false,
  selected: false,
  external: false,
  link: '',
});

const className = computed(() => {
  return {
    'dropdown-item': true,
    'dropdown-item--default': !props.custom,
    'dropdown-item--separated': props.separated,
    'dropdown-item--selected': props.selected,
    'dropdown-item--muted': props.muted,
  };
});
const type = computed(() => (props.link ? (props.external ? 'a' : 'NuxtLink') : 'div'));

/**
 * Links starts from http is automatically external, but
 * we still have a case when link should be outside vue-router
 */
const isExternal = computed(() => {
  if (typeof props.link === 'string') {
    return props.link?.match(/^https?:\/\//) || props.external;
  }
  return props.external;
});

const rootProps = computed(() => {
  if (props.link && isExternal.value) return { href: props.link };
  if (props.link) return { to: props.link };

  return {};
});
</script>

<style scoped lang="scss">
.dropdown-item {
  $this: &;

  display: block;
  text-decoration: none;
  transition: background-color, color 0.15s ease-in-out;

  &--default {
    @include body;

    padding: 12px 16px;
    font-weight: normal;
    color: var(--theme-color-text);
    background-color: var(--theme-color-card);
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      color: var(--theme-color-text-hover);
      background-color: var(--theme-color-over-card);
    }
  }

  &--selected {
    background-color: var(--theme-color-over-card);
  }

  & + & {
    border-top: solid 1px var(--theme-color-border);

    &#{$this}--separated {
      border-color: var(--theme-color-background);
    }
  }

  &--separated {
    border-top: solid 1px var(--theme-color-background);
  }

  &--muted {
    color: var(--gray-50);
  }
}
</style>
